import type { Attachment } from '@/lib/attachments-queries';

export interface ThreadNode {
  message: Attachment;
  attachedDocs: Attachment[];
  children: ThreadNode[];
}

/**
 * Build a branching message tree from the server's oldest-first flat thread
 * response. Roots are messages with no parent, or with a parent missing from
 * the provided list. Children preserve input order.
 */
export function buildThreadTree(
  messages: ReadonlyArray<{ message: Attachment; attachedDocs: Attachment[] }>,
): ThreadNode[] {
  const byId = new Map<string, ThreadNode>();
  const ids = new Set(messages.map((m) => m.message.id));

  for (const m of messages) {
    byId.set(m.message.id, {
      message: m.message,
      attachedDocs: m.attachedDocs,
      children: [],
    });
  }

  const roots: ThreadNode[] = [];
  for (const m of messages) {
    const node = byId.get(m.message.id)!;
    const parentId = m.message.inReplyToId;
    if (parentId && ids.has(parentId)) {
      const parent = byId.get(parentId);
      if (parent) {
        parent.children.push(node);
        continue;
      }
    }
    roots.push(node);
  }

  return roots;
}
